import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import GradeBadge from "../../../components/GradeBadge";
import ExamList from "../../../components/ExamList";
import { api, gradeFor } from "../../../lib/api";
export default function StudentReport() {
    const [sp, setSp] = useSearchParams();
    const classId = parseInt(sp.get("class") ?? "10", 10);
    const studentId = sp.get("student") ?? "";
    const [classes, setClasses] = useState([]);
    const [students, setStudents] = useState([]);
    const [rows, setRows] = useState(null);
    const [student, setStudent] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            const { classes } = await api.get("/api/classes");
            setClasses(classes);
        })();
    }, []);
    useEffect(() => {
        (async () => {
            setLoading(true);
            const { students } = await api.get(`/api/classes/${classId}/students`);
            setStudents(students);
            if (!studentId && students[0]) {
                setSp({ class: String(classId), student: students[0].id }, { replace: true });
            }
            setLoading(false);
        })();
    }, [classId]);
    useEffect(() => {
        if (!studentId) {
            setStudent(null);
            setRows(null);
            return;
        }
        (async () => {
            setLoading(true);
            const { student, rows } = await api.get(`/api/students/${studentId}/scores`);
            setStudent(student);
            setRows(rows);
            setLoading(false);
        })();
    }, [studentId]);
    // Percentage-based average across exams that may have different max marks.
    const avgPct = useMemo(() => {
        if (!rows)
            return null;
        const pcts = [];
        for (const r of rows) {
            const max = r.part1_max + r.part2_max;
            if (typeof r.total === "number" && max > 0)
                pcts.push((r.total / max) * 100);
        }
        return pcts.length ? pcts.reduce((a, b) => a + b, 0) / pcts.length : null;
    }, [rows]);
    const download = () => {
        if (!student || !rows)
            return;
        const header = [
            ["IELL Karaitivu — Student report"],
            ["Student", student.full_name, "Class", student.class_name],
            [],
            ["Exam", "Date", "Part 1", "Part 2", "Total", "Grade"],
        ];
        const body = rows.map((r) => [
            r.exam_name,
            r.held_on ?? "",
            r.part1 ?? "",
            r.part2 ?? "",
            r.total ?? "",
            r.grade ?? "",
        ]);
        if (avgPct != null)
            body.push([], ["Average %", "", "", "", Number(avgPct.toFixed(2)), gradeFor(avgPct, 100) ?? ""]);
        const ws = XLSX.utils.aoa_to_sheet([...header, ...body]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");
        XLSX.writeFile(wb, `IELL_${student.full_name.replace(/\W+/g, "_")}_report.xlsx`);
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex items-center gap-2 mb-4 flex-wrap no-print", children: [_jsx("select", { className: "input w-auto", value: classId, onChange: (e) => setSp({ class: e.target.value, student: "" }), children: classes.map((c) => _jsx("option", { value: c.id, children: c.name }, c.id)) }), _jsxs("select", { className: "input w-auto min-w-[220px]", value: studentId, onChange: (e) => setSp({ class: String(classId), student: e.target.value }), children: [_jsx("option", { value: "", children: "\u2014 pick student \u2014" }), students.map((s) => _jsxs("option", { value: s.id, children: [s.roll_no, ". ", s.full_name] }, s.id))] }), _jsx("div", { className: "flex-1" }), _jsx("button", { className: "btn-ghost text-xs", onClick: () => window.print(), disabled: !student, children: "Print" }), _jsx("button", { className: "btn text-xs", onClick: download, disabled: !student, children: "Download xlsx" })] }), loading ? (_jsx("p", { className: "text-slate2 text-sm", children: "Loading\u2026" })) : !student ? (_jsx("p", { className: "text-slate2 text-sm", children: "Pick a student to see their report." })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "mb-4 flex items-center gap-4 flex-wrap", children: [_jsxs("div", { children: [_jsx("div", { className: "eyebrow", children: "Student report" }), _jsx("h2", { className: "font-display text-2xl mt-1", children: student.full_name }), _jsx("div", { className: "text-slate2 text-sm", children: student.class_name })] }), avgPct != null && (_jsxs("div", { className: "ml-auto flex items-center gap-3 card px-4 py-2 no-print", children: [_jsx("div", { className: "text-xs text-slate2 uppercase tracking-wider", children: "Average" }), _jsxs("div", { className: "font-mono text-2xl", children: [avgPct.toFixed(1), _jsx("span", { className: "text-slate2 text-sm ml-1", children: "%" })] }), _jsx(GradeBadge, { g: gradeFor(avgPct, 100) })] }))] }), rows && _jsx(ExamList, { rows: rows })] }))] }));
}
